import { ChangeDetectionStrategy, Component, signal, viewChild } from "@angular/core";
import { Item } from "../../../pojo/Item";
import { S25ItemComponent } from "../../s25-item/s25.item.component";
import { Table } from "../../s25-table/Table";
import { GenericTableButtonComponent } from "../../s25-table/generics/generic.table.button.component";
import { EventQuotaService } from "../../../services/event.quota.service";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { GenericTableListComponent } from "../../s25-table/generics/generic.table.list.component";
import { S25ModalComponent } from "../../s25-modal/s25.modal.component";
import { EventQuota } from "../../../pojo/EventQuota";
import { S25EditEventQuotasComponent } from "./s25.edit.event.quotas.component";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { TelemetryEvent, TelemetryService } from "../../../services/telemetry.service";
import { CacheRepository } from "../../../decorators/cache.decorator";
import { UserprefService } from "../../../services/userpref.service";

const EMPTY_MAX_TIME = "P0DT00H00M";

function isoDurationToMaxTimeHoursMinutes(isoDuration: string | null | undefined): {
    hours: number | null;
    minutes: number | null;
} {
    if (!isoDuration || isoDuration === EMPTY_MAX_TIME) {
        return { hours: null, minutes: null };
    }
    const totalMinutes = S25Util.ISODurationToMinutes(isoDuration);
    if (totalMinutes <= 0) {
        return { hours: null, minutes: null };
    }
    return {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
    };
}

function formatMaxTimeLabel(hours: number | null | undefined, minutes: number | null | undefined): string {
    const hoursValue = hours ?? 0;
    const minutesValue = minutes ?? 0;
    if (hoursValue <= 0 && minutesValue <= 0) {
        return "";
    }
    const parts: string[] = [];
    if (hoursValue > 0) {
        parts.push(`${hoursValue} Hr${hoursValue === 1 ? "" : "s"}`);
    }
    if (minutesValue > 0) {
        parts.push(`${minutesValue} Min${minutesValue === 1 ? "" : "s"}`);
    }
    return parts.join(" and ");
}

function formatMaxTimeLabelFromIso(isoDuration: string | null | undefined): string {
    const { hours, minutes } = isoDurationToMaxTimeHoursMinutes(isoDuration);
    return formatMaxTimeLabel(hours, minutes);
}

type ChosenQuota = { isNew: boolean; title: string; model: EventQuota.Detailed };

@TypeManagerDecorator("s25-ng-event-quotas-list")
@Component({
    selector: "s25-ng-event-quotas-list",
    template: `
        <div class="top">
            <div class="header">
                <button class="aw-button aw-button--primary" (click)="onCreateClick()">Add Quota</button>
            </div>
            <s25-ng-table
                [dataSource]="tableData"
                [hasRefresh]="true"
                [hasFilter]="true"
                [hasColumnChooser]="true"
                [columnSortable]="true"
                [unlimitedWidth]="true"
            ></s25-ng-table>
            <s25-ng-modal
                #editQuota
                [title]="chosenQuota()?.title"
                [type]="'save'"
                (save)="onSave()"
                (cancel)="onEditModalCancel()"
                [size]="'lg'"
            >
                <ng-template #s25ModalBody>
                    @if (chosenQuota(); as quota) {
                        <s25-ng-edit-event-quotas
                            #quotaComponent
                            [isNew]="quota.isNew"
                            [model]="quota.model"
                        ></s25-ng-edit-event-quotas>
                    }
                </ng-template>
            </s25-ng-modal>
            <s25-ng-modal #deleteQuota [title]="'Confirm Deletion'" [type]="'deny'" [size]="'xs'">
                Are you sure that you want to delete this quota?
            </s25-ng-modal>
        </div>
    `,
    styles: `
        :host {
            display: block;
            min-width: 0;
        }

        .header > button {
            margin-bottom: 1em;
        }

        ::ng-deep s25-ng-event-quotas-list s25-item s25-popover {
            display: block !important;
            color: var(--color--link-text);
            cursor: pointer;
        }

        .buttons {
            display: flex;
            gap: 0.5em;
            justify-content: flex-end;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EventQuotasListComponent {
    readonly quotas = new Map<number, EventQuota.Detailed>();
    readonly chosenQuota = signal<ChosenQuota | undefined>(undefined);

    readonly tableComponent = viewChild(S25TableComponent);
    readonly editQuotaModal = viewChild<S25ModalComponent>("editQuota");
    readonly deleteQuotaModal = viewChild<S25ModalComponent>("deleteQuota");
    readonly quotaComponent = viewChild<S25EditEventQuotasComponent>("quotaComponent");

    readonly mapToRow = (item: EventQuota.Detailed, dateFormat: string): Table.Row => {
        const organizations: Table.Cell[] = [
            ...(item.organizations ?? []).map(({ itemName, itemTypeId, itemId }) => ({
                component: S25ItemComponent,
                inputs: {
                    itemName,
                    itemTypeId: itemTypeId ?? Item.Ids.Organization,
                    itemId,
                },
            })),
            ...(item.organizationSearches ?? []).map(({ itemName }) => ({
                text: `Search: ${itemName}`,
            })),
        ];
        const organizationLabels = [
            ...(item.organizations ?? []).map((organization) => organization.itemName),
            ...(item.organizationSearches ?? []).map((search) => `Search: ${search.itemName}`),
        ];
        const locations: Table.Cell[] = item.locations.map(({ itemName, itemId, itemTypeId }) => ({
            component: S25ItemComponent,
            inputs: { itemName, itemTypeId: itemTypeId ?? Item.Ids.Location, itemId },
        }));
        const eventTypes: Table.Cell[] = (item.eventTypes ?? []).map(({ itemName, itemId, itemTypeId }) => ({
            component: S25ItemComponent,
            inputs: { itemName, itemTypeId: itemTypeId ?? Item.Ids.EventType, itemId },
        }));
        return {
            id: item.itemId,
            name: item.itemName,
            cells: {
                name: {
                    text: item.itemName,
                },
                type: {
                    text: item.type.itemName.replace("Organization", "Org"),
                },
                state: {
                    text: item.active ? "Active" : "Inactive",
                },
                included_reservations: {
                    text: item.skipExisting ? "Single Event" : "All Occurrences",
                },
                counting_unit: {
                    text: item.countByEvent ? "Event" : "Occurrence",
                },
                include_requests: {
                    text: (item.locations?.length ?? 0) > 0 ? (item.includeRequests ? "Yes" : "No") : "",
                },
                occurrences: {
                    text: item.quantity || "",
                },
                time: {
                    inputs: {
                        items: formatMaxTimeLabelFromIso(item.maxTime)
                            .split(" and ")
                            .filter(Boolean)
                            .map((durationPart) => ({ text: durationPart })),
                        hasDecoration: false,
                    },
                },
                time_window: {
                    text: EventQuota.timeWindowLabel(item, dateFormat),
                },
                schedule: {
                    text: EventQuota.scheduleSummaryLabel(item, (time) => time),
                },
                organizations: {
                    inputs: { items: organizations },
                    textValue: organizationLabels.join(" "),
                },
                locations: {
                    inputs: { items: locations },
                    textValue: item.locations.map((location) => location.itemName).join(" "),
                },
                event_types: {
                    inputs: { items: eventTypes },
                    textValue: (item.eventTypes ?? []).map((eventType) => eventType.itemName).join(" "),
                },
            },
        };
    };

    readonly getRows = async (query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> => {
        if (query.forceRefresh) {
            CacheRepository.invalidateByService("EventQuotaService", "getAll");
        }
        const [quotaList, dateFormat] = await Promise.all([
            EventQuotaService.getAll(),
            UserprefService.getS25Dateformat(),
        ]);
        for (const quota of quotaList || []) {
            this.quotas.set(quota.itemId, quota);
        }

        return {
            rows: (quotaList || []).map((quota) => this.mapToRow(quota, dateFormat)),
        };
    };

    readonly onCopyClick = async (row: Table.Row) => {
        const quota = S25Util.deepCopy(this.quotas.get(row.id as number));

        if (quota) {
            delete quota.itemId;
            quota.itemName += " copy";

            const chosen: ChosenQuota = {
                isNew: true,
                title: `Copy Quota: ${row.name}`,
                model: quota,
            };

            this.sendTelemetryEvent("QuotaCopy", quota);
            this.openQuotaEditor(chosen);
        }
    };

    readonly onEditClick = (row: Table.Row) => {
        const quota = S25Util.deepCopy(this.quotas.get(row.id as number));
        if (!quota) {
            return;
        }
        const chosen: ChosenQuota = {
            isNew: false,
            title: `Edit Quota: ${row.name}`,
            model: quota,
        };

        this.sendTelemetryEvent("QuotaEdit", quota);
        this.openQuotaEditor(chosen);
    };

    readonly onDeleteClick = async (row: Table.Row) => {
        const yes = await this.deleteQuotaModal()?.open();
        if (yes) {
            const quota = this.quotas.get(row.id as number);
            quota && this.sendTelemetryEvent("QuotaDelete", quota);
            await EventQuotaService.delete([row.id as number]);
            this.tableComponent()?.refresh();
        }
    };

    readonly tableData: Table.DataSource = {
        type: "unpaginated",
        dataSource: this.getRows,
        columns: [
            {
                id: "name",
                header: "Name",
            },
            {
                id: "type",
                header: "Type",
            },
            {
                id: "state",
                header: "State",
            },
            {
                id: "included_reservations",
                header: "Included Occurrences",
            },
            {
                id: "counting_unit",
                header: "Counting Unit",
            },
            {
                id: "include_requests",
                header: "Include Requests",
            },
            {
                id: "occurrences",
                header: "Max Occurr\u00ADences",
            },
            {
                id: "time",
                header: "Max Time",
                cells: { component: GenericTableListComponent },
            },
            {
                id: "time_window",
                header: "Time Window",
            },
            {
                id: "schedule",
                header: "Schedule",
            },
            {
                id: "organizations",
                header: "Organizations",
                cells: { component: GenericTableListComponent },
            },
            {
                id: "locations",
                header: "Locations",
                cells: { component: GenericTableListComponent },
            },
            {
                id: "event_types",
                header: "Event Types",
                cells: { component: GenericTableListComponent },
            },
            GenericTableButtonComponent.Column("Copy", this.onCopyClick, "outline", 75),
            GenericTableButtonComponent.Column("Edit", this.onEditClick, "outline", 75),
            GenericTableButtonComponent.Column("Delete", this.onDeleteClick, "danger--outline", 75),
        ],
    };

    async onCreateClick(): Promise<void> {
        const chosen: ChosenQuota = { isNew: true, title: "Create New Quota", model: { itemName: "New Quota" } };
        this.sendTelemetryEvent("QuotaAdd", chosen.model);
        this.openQuotaEditor(chosen);
    }

    async onSave(): Promise<void> {
        const saved = await this.quotaComponent()?.save();
        if (saved) {
            this.sendTelemetryEvent("QuotaSave", saved);
            this.mergeSavedQuotaIntoCache(saved);
            this.editQuotaModal()?.close();
            this.chosenQuota.set(undefined);
            void this.tableComponent()?.refresh(true);
        } else {
            this.editQuotaModal()?.stopLoading();
        }
    }

    onEditModalCancel(): void {
        this.quotaComponent()?.close("cancel");
        this.chosenQuota.set(undefined);
    }

    private openQuotaEditor(chosen: ChosenQuota): void {
        this.chosenQuota.set(chosen);
        void this.editQuotaModal()?.open();
    }

    private mergeSavedQuotaIntoCache(saved: EventQuota.Detailed): void {
        if (!saved.itemId) {
            return;
        }
        const previous = this.quotas.get(saved.itemId);
        this.quotas.set(saved.itemId, {
            ...previous,
            ...saved,
            type: saved.type ?? previous?.type,
            organizations: saved.organizations ?? previous?.organizations ?? [],
            organizationSearches: saved.organizationSearches ?? previous?.organizationSearches ?? [],
            locations: saved.locations ?? previous?.locations ?? [],
            eventTypes: saved.eventTypes ?? previous?.eventTypes ?? [],
            secGroups: saved.secGroups ?? previous?.secGroups ?? [],
        });
    }

    private sendTelemetryEvent(
        action: "QuotaAdd" | "QuotaCopy" | "QuotaEdit" | "QuotaDelete" | "QuotaSave",
        quota: EventQuota.Detailed,
    ): void {
        const typeId = EventQuota.toQuotaTypeId(quota.type?.itemId);
        const subCategory: "QuotaOrganization" | "QuotaUser" =
            typeId === EventQuota.TypeIds.Organization ? "QuotaOrganization" : "QuotaUser";

        const telemetryEvent: TelemetryEvent = {
            eventCategory: "Quotas",
            subCategory: subCategory,
            eventType: action,
        };

        TelemetryService.sendEvent(telemetryEvent);
    }
}
